import { useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, CaretRight, Folder, FolderOpen, Plus, PushPin, PushPinSlash, X } from '@phosphor-icons/react';

// UX-2.5D · フォルダーはコンテンツ側の場所ナビゲーションです。
// 既定は重ねて開くパネル（選ぶと閉じる）。書類一覧が常に本来の幅を使えます。
// 「固定」を選んだ利用者だけ、一覧の左に細い列として常時表示します。
function flatten(roots, childrenOf, expanded) {
  const out = [];
  const walk = (nodes, depth, trail) => {
    nodes.forEach((f) => {
      const kids = childrenOf(f.id);
      const path = [...trail, f.name];
      out.push({ f, depth, path, kids: kids.length, open: expanded.has(f.id) });
      if (expanded.has(f.id) && kids.length) walk(kids, depth + 1, path);
    });
  };
  walk(roots, 0, []);
  return out;
}

export default function FolderTreePanel({ roots, childrenOf, folderId, canManage,
  pinned, open, onOpenFolder, onNewFolder, onTogglePin, onClose }) {
  // 既定で最上段を開く。閉じたままだと「下に何かある」ことに気付けない。
  const [expanded, setExpanded] = useState(() => new Set());
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !(roots || []).length) return;
    seeded.current = true;
    setExpanded(new Set((roots || [])
      .filter((f) => (childrenOf(f.id) || []).length)
      .map((f) => f.id)));
  }, [roots, childrenOf]);
  const panelRef = useRef(null);

  const rows = useMemo(
    () => flatten(roots || [], childrenOf, expanded),
    [roots, childrenOf, expanded],
  );

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pinned, open, onClose]);

  const pick = (id) => {
    onOpenFolder(id);
    if (!pinned) onClose?.();
  };

  const list = (
    <div className="space-y-0.5" data-testid="dms-folder-tree">
      {rows.length === 0 ? (
        <p className="px-2 py-2 text-sm text-zinc-400">フォルダーはまだありません</p>
      ) : null}
      {rows.map((row) => {
        const active = folderId === row.f.id;
        return (
          <div key={row.f.id} className="relative flex items-center"
            data-testid={'dms-tree-row-' + row.f.id}>
            {/* 入れ子が一目で分かるように、階層ごとに縦の案内線を引く */}
            {Array.from({ length: row.depth }).map((_x, d) => (
              <span key={d} aria-hidden="true"
                className="shrink-0 self-stretch border-l border-zinc-200"
                style={{ width: 14, marginLeft: d === 0 ? 10 : 0 }} />
            ))}
            {row.kids > 0 ? (
              <button type="button" onClick={() => toggle(row.f.id)}
                aria-label={row.f.name + 'の下位フォルダー'}
                aria-expanded={row.open}
                data-testid={'dms-tree-toggle-' + row.f.id}
                className="inline-flex items-center justify-center h-8 w-6 shrink-0 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none">
                {row.open ? <CaretDown size={12} /> : <CaretRight size={12} />}
              </button>
            ) : (
              <span className="w-6 shrink-0" aria-hidden="true" />
            )}
            <button type="button" onClick={() => pick(row.f.id)}
              title={row.path ? row.path.join(' / ') : row.f.name}
              data-testid={'dms-tree-' + row.f.id}
              className={'flex-1 min-w-0 flex items-center gap-2 rounded-md px-2 min-h-[36px] text-sm text-left transition-colors '
                + (active ? 'bg-teal-50 text-teal-900 font-semibold' : 'text-zinc-700 hover:bg-zinc-100')}>
              {active
                ? <FolderOpen size={16} weight="duotone" className="text-teal-700 shrink-0" />
                : <Folder size={16} weight="duotone" className={row.depth ? 'text-zinc-300 shrink-0' : 'text-zinc-400 shrink-0'} />}
              <span className="truncate">{row.f.name}</span>
              {/* D6e — ここは「下位フォルダーの数」を出していたので、書類が30件
                  あるフォルダーが 0 と見えていた。サブフォルダーを含む書類数を出す。 */}
              {(row.f.document_count_total ?? row.f.document_count ?? 0) > 0 && (
                <span className="ml-auto shrink-0 rounded-full bg-zinc-100 px-1.5 text-[10px] tabular-nums text-zinc-500"
                  data-testid={'dms-tree-count-' + row.f.id}
                  title={`書類 ${row.f.document_count_total ?? 0}件（サブフォルダー含む・直下 ${row.f.document_count ?? 0}件）`}>
                  {row.f.document_count_total ?? row.f.document_count}
                  {(row.f.document_count_total ?? 0) > (row.f.document_count ?? 0) ? '＋' : ''}
                </span>
              )}
            </button>
          </div>
        );
      })}
      {canManage ? (
        <button type="button" onClick={onNewFolder} data-testid="dms-new-folder-btn"
          className="w-full flex items-center gap-1.5 px-2 min-h-[36px] rounded-md text-sm text-zinc-500 hover:bg-zinc-100 text-left focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none">
          <Plus size={14} /> 新規フォルダー
        </button>
      ) : null}
    </div>
  );

  const head = (
    <div className="flex items-center justify-between gap-2 mb-2">
      <p className="px-1 text-xs font-semibold text-zinc-500">フォルダー</p>
      <div className="flex items-center gap-1">
        <button type="button" onClick={onTogglePin}
          aria-label={pinned ? 'フォルダーの固定を解除する' : 'フォルダーを左に固定する'}
          title={pinned ? '固定を解除' : '左に固定'}
          aria-pressed={!!pinned}
          data-testid="dms-folder-pin-toggle"
          className="hidden xl:inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-500 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none">
          {pinned ? <PushPinSlash size={16} /> : <PushPin size={16} />}
        </button>
        {!pinned && (
          <button type="button" onClick={onClose}
            aria-label="フォルダーを閉じる" title="閉じる"
            data-testid="dms-folder-drawer-close"
            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-500 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none">
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {pinned && !open && (
        <div className="hidden xl:block w-56 shrink-0 self-start" data-testid="dms-folder-inline">
          {head}
          {list}
        </div>
      )}
      {open && (
        <div className={`fixed inset-0 z-50 flex${pinned ? ' xl:hidden' : ''}`} data-testid="dms-folder-drawer">
          <button type="button" aria-label="閉じる" onClick={onClose}
            className="absolute inset-0 bg-zinc-900/30" data-testid="dms-folder-drawer-backdrop" />
          <div ref={panelRef} role="dialog" aria-label="フォルダー"
            className="relative w-[320px] max-w-[85vw] bg-white h-full shadow-xl p-3 overflow-y-auto">
            {head}
            {list}
          </div>
        </div>
      )}
    </>
  );
}
